import React, { FormEvent, useEffect, useState } from 'react';
import Input from '../../../../components/input';
import Form from '../../../../components/_base/Form';
import Button from '../../../../components/button';
import { registerAttendee } from '../../../../lib/api/public';
import ErrorBlock from '../../../../components/_base/ErrorBlock';
import { CustomFieldsResponseType, PublicSessionMeetingDetails } from '../../../../lib/api';
import {
  ValuesProps,
  TouchedProps,
  ErrorProps,
  ValidatorType,
  settings,
  useValues,
  Formatter,
  FieldsProps
} from './field-settings';
import { getFormattedValue } from '../../../../hooks/use-form-field';
import './RegistrationForm.scss';
import SelectBox from '../../../shared/SelectBox';
import SessionChoices from './SessionChoices';
import TextAsHTML from '../../../../components/_base/TextAsHTML';
import API, { PUBLIC } from '../../../../lib/api';

export type IRegistrationFormProps = {
  registrationSubscription: (response: { registrationId: string, appSyncToken: string }) => void;
  eventId: number;
  toggleRegistrationForm: () => void;
  showSubmitMessage: (v: boolean) => void;
  setErrorRes: (v: boolean) => void;
  customFields: CustomFieldsResponseType[];
  setEmailText: (v: string) => void;
  registrationHeader: string;
  registrationInstructions: string;
  setEventToken: (eventToken: string) => void;
  location: { from?: string, errorMsg?: string; email?: string };
  isProRegistrationView: boolean;
  autoSignInEnabled: boolean;
  generalSessionList: PublicSessionMeetingDetails[];
  eventTimeZone: string;
  publicDescription: string;
};

type InputProps = {
  i: number;
  label: string;
  name: string;
  values: ValuesProps;
  touched: TouchedProps;
  errors: ErrorProps;
  optional?: boolean;
  validators: ValidatorType[];
  formatters: Formatter[];
  handleChange: (
    key: string,
    value: string,
    validators: ValidatorType[]) => void;
  handleBlur: (
    key: string,
    value: string,
    validators: ValidatorType[]) => void;
  withOverflowEllipsis?: boolean,
  email?: string;
};

export enum CustomFieldType {
  text = 'Text',
  first_name = 'firstName',
  last_name = 'lastName',
  dropdown = 'DROPDOWN',
  checkbox = 'CHECKBOX'
}

const InputBox: React.FC<InputProps> = (props: InputProps): JSX.Element => {
  const {
    i, name, label, optional, values,
    validators, touched, errors, withOverflowEllipsis,
    handleBlur, handleChange, formatters, email
  } = props;

  useEffect(() => {
    if (email) handleChange('email', email, validators);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (e?: React.ChangeEvent<HTMLInputElement>) => {
    const value = getFormattedValue((e?.target?.value ?? ''), formatters);
    handleChange(name, value, validators);
  };

  const onBlur = (name: string) => {
    handleBlur(name, values[name], validators);
  };

  const checkErrors = touched[name] && (!errors[name].map(({ valid }) => valid).every(Boolean) || (!optional && !values[name]));

  const error = touched[name] && errors[name].find(err => !err.valid);
  return (
    <div className='mt-3 word-break' key={`${i}_${name}`}>
      <Input.Text
        id={name}
        name={name}
        label={label}
        onChange={onChange}
        error={checkErrors}
        value={values[name]}
        onBlur={() => onBlur(name)}
        span={optional ? '(optional)' : undefined}
        withOverflowEllipsis={withOverflowEllipsis}
      />
      <div className="registration-error-margin absolute">
        { error && <ErrorBlock errorMessage={error.validationMessage || ''} /> }
      </div>
    </div>
  );
};

const RegistrationForm: React.FC<IRegistrationFormProps> = (props: IRegistrationFormProps): JSX.Element => {
  const { eventTimeZone, generalSessionList, publicDescription, isProRegistrationView } = props;
  const [ selectedMeetings, setSelectedMeetings] = useState<number[]>([]);
  const {
    state,
    handleChange,
    handleBlur
  } = useValues(settings(props.customFields));

  const { values, touched, errors, isDisabledSubmit } = state;

  const subscribeToEventRegistration = async (eventId: string): Promise<string | null> => {
    try {
      const registrationParams = await API[PUBLIC].fetchRegistrationSubscriptionParams(eventId);
      props.registrationSubscription(registrationParams);

      return registrationParams.registrationId;
    } catch {
      return null;
    }
  };

  const registrationHandle = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      const eventId = props.eventId.toString();
      const params = {
        ...Object.keys(values).reduce<{[key: string]: string | null}>((result, key) => {
          result[key] = values[key].trim() || null;
          return result;
        }, {}),
        sessionMeetingIds: selectedMeetings
      };

      const registrationId = props.autoSignInEnabled ? await subscribeToEventRegistration(eventId) : null;

      if (registrationId) Reflect.set(params, 'registrationId', registrationId);

      const response = await registerAttendee(eventId, params);

      props.showSubmitMessage(true);
      props.setEmailText(values['email']);
      props.toggleRegistrationForm();
      response.eventToken && props.setEventToken(response.eventToken);
    } catch (e) {
      props.showSubmitMessage(false);
      props.setErrorRes(true);
    }
  };

  const className = 'editable-sign-in';
  const classNameRegistrationTitle = `${className}--title`;
  const classNameRegistrationSubmitButton = `${className}--submit-button`;
  const fields = settings(props.customFields);

  const formattedFields = fields.reduce((accumulator , currentValue: FieldsProps) => {
    const [singleFields, nameGroup] = accumulator as [FieldsProps[], FieldsProps];
    const { order } = currentValue;
    const isNameField =
      currentValue.name === CustomFieldType.first_name ||
      currentValue.name === CustomFieldType.last_name;

    return isNameField ?
      [
        singleFields,
        nameGroup.group?.length ?
          {
            ...nameGroup,
            group:  nameGroup.group.concat(currentValue)
          } :
          {
            ...nameGroup,
            group: [currentValue],
            order: order,
          }
      ] :
      [ singleFields.concat(currentValue), nameGroup ];
  }, [[], {}]);

  return (
    <Form onSubmit={registrationHandle} name='RegistrationForm'>
      { !isProRegistrationView && <h1 className={`${classNameRegistrationTitle} h1-font-size leading-10 break-words`}>
        {props.registrationHeader || 'Register'}
      </h1> }
      { isProRegistrationView && (
        <div className='registration-form__public-description'>
          <TextAsHTML formattedText={publicDescription} />
        </div>
      )}
      {props.location?.errorMsg &&
        <div className='mt-3'>
          <span className='break-words font-bold'>
            {props.location.errorMsg}
          </span>
        </div>}
      <div className={`mt-3 ${isProRegistrationView ? 'registration-form__instructions--pro-view' : ''}`}>
        <span
          className='break-words'
          dangerouslySetInnerHTML={{ __html: props.registrationInstructions ?? '' }}
        >
        </span>
      </div>

      {([...formattedFields[0] as FieldsProps[], formattedFields[1] as FieldsProps])
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((field, i) => {
          if (field.fieldType === CustomFieldType.dropdown || field.fieldType === CustomFieldType.checkbox) {
            return (
              <SelectBox
                handleBlur={handleBlur}
                handleChange={handleChange}
                key={field.name}
                field={field}
                values={values}
                validators={field.validators ?? []}
                errors={errors}
                touched={touched}
                optional={field.optional}
              />
            );
          } else if (field.group) {
            return <div className='flex justify-between input-group' key={i}>
              {
                field.group.map((fieldInGroup: FieldsProps, j: number) => {
                  return (
                    <InputBox
                      i={j}
                      key={`group-${j}`}
                      name={fieldInGroup?.name}
                      label={fieldInGroup?.label}
                      touched={touched}
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      optional={fieldInGroup?.optional}
                      handleChange={handleChange}
                      validators={fieldInGroup?.validators ?? []}
                      formatters={fieldInGroup?.formatters ?? []}
                    />
                  );
                })
              }
            </div>;
          } else {
            if (field.label === "Entry Fee") {
              return (
                <div id={`supplementary-info`}>
                  <ul>
                    <li><span>CFA Society Korea 회원:</span> 5만원</li>
                    <li><span>일반 참가자:</span> 10만원</li>
                  </ul>
                  <p><b>[계좌번호]</b></p>
                  <p>신한은행: 140-013-547575</p>
                  <p>예금주: 씨에프에이한국협회(CFA한국협회)</p>
                  <br></br>
                  <p>▶ 입금확인증 발급이 필요하신 경우 아래 해당되는 사항에 체크해주시기 바랍니다.</p> 
                  <p>(입금 확인증은 행사 후 7일 이내 일괄 발송예정)</p>
                </div>
              );
            }
            else if (field.label === "SI 1") {
              return (
                <div id={`supplementary-info-1`}>
                  <br></br>
                  <p>참가신청 후 48시간 이내에 참가비를 입금해야 등록절차가 완료됩니다.</p> 
                  <p>모든 등록절차가 완료되면, 4일 이내에 이메일로 참가신청 완료 이메일이 전달됩니다.</p> 
                  <br></br>
                  <p>환불신청은 <a href={`mailto:info@cfasocietykorea.org`}>info@cfasocietykorea.org</a>로만 접수 가능합니다.</p>
                  <p>환불요청 시 성함/전화번호/환불 받을 계좌번호를 기재해주시기 바랍니다. (환불은 행사 후 7일 이내 일괄처리 예정)</p>
                  <br></br>
                  <p>환불 규정</p>
                  <p>-전액 환불(100%): 9월 20일(화)까지 환불 신청</p>
                  <p>-부분 환불(50%): 9월 25일(일)까지 환불 신청</p>
                  <p>-환불 불가: 9월 26일 이후 환불 신청</p>
                </div>
              );
            }
            else {
              return (
                <InputBox
                  i={i}
                  key={i}
                  values={values}
                  errors={errors}
                  name={field.name}
                  touched={touched}
                  label={field.label}
                  handleBlur={handleBlur}
                  optional={field.optional}
                  handleChange={handleChange}
                  validators={field.validators ?? []}
                  formatters={field.formatters ?? []}
                  email={props.location?.email}
                />
              );
            }
          }
        })}
      { !!generalSessionList?.length && (
        <SessionChoices
          data={generalSessionList}
          timeZone={eventTimeZone}
          onChange={(meetingIds: number[]) => setSelectedMeetings(meetingIds)}
          selectedMeetings={selectedMeetings}
        />
      )}
      <div className={`${classNameRegistrationSubmitButton} flex justify-end mt-6`}>
        <Button.Submit
          name='registration'
          label='Register(등록하기)'
          size={'big'}
          disabled={isDisabledSubmit}
          className='min-w-full px-5 py-3 rounded bg-primary text-white on-hover-shadow'
        />
      </div>
      <div id="acknowledgement">
        <p>
          By clicking submit, you agree to the privacy policies of CFA Society Korea <a href="https://www.cfasocietykorea.org/privacy-policy" target="_blank" rel="noopener noreferrer">https://www.cfasocietykorea.org/privacy-policy</a> and CFA Institute (<a href="https://www.cfainstitute.org/en/about/governance/policies/privacy-policy" target="_blank" rel="noopener noreferrer">https://www.cfainstitute.org/en/about/governance/policies/privacy-policy</a>). You may withdraw your consent following the instructions in the Privacy Policies at any time.
        </p>
        <p>
          행사관련 문의 <a href="mailto:info@cfasocietykorea.org">info@cfasocietykorea.org</a>
        </p>
        <img src="https://s3.amazonaws.com/present.knowledgevision.com/account/5d0f02d2-326a-4d58-8cc7-2782fee22429/content/assets/templates/template_background/2022_KIC_SPONSORS_1663313686901/2022_KIC_SPONSORS_aaa049f6-fd11-4f28-a80a-53511ef5d96d.png?AWSAccessKeyId=AKIAJ3VQS6HPMVD4IBQA&Expires=2145916800&Signature=vrNI2XLPeq%2BazYc32afl2%2FosEpA%3D" alt="sponsors"></img>
      </div>
    </Form>

  );
};

export default RegistrationForm;
